import {
    ConfirmedPaymentMethodResponseDto,
    CustomFieldZoneResponseDto,
    DigitalResourceResponseDto,
    OrderCustomFieldResponseDto,
    OrderItemResponseDto,
    OrderResponseDto,
    PersonalAddressResponseDto,
    PickupLocationResponseDto,
} from 'client/widget-components/ecom/types/EcomSDK';
import { DataRow } from './components/DataCard';
import { EcommStr } from './useEcommStr';

/**
 * Starts a download by clicking a detached anchor.
 *
 * `download` is honoured only on same-origin URLs. A cross-origin response
 * (e.g. a redirect to a presigned S3 URL) relies on the server's
 * `Content-Disposition`, so the anchor opens a new tab: a file served inline
 * then renders there instead of navigating the current page away.
 */
export function downloadFromUrl(url: string, filename?: string): void {
    const anchor = document.createElement('a');
    anchor.href = url;
    if (filename) anchor.download = filename;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
}

/**
 * Resources belong to digital products only; ignore them elsewhere even if the
 * API ever attaches them to another item type.
 */
export function digitalResourcesOf(
    item: OrderItemResponseDto
): DigitalResourceResponseDto[] {
    if (item.type !== 'DIGITAL_PRODUCT') return [];
    return item.digital_resources ?? [];
}

export function formatCurrency(amount: number, currency: string): string {
    const ecomm = typeof window !== 'undefined' ? window._ecomm : undefined;
    if (ecomm?.formatMoney) {
        try {
            return ecomm.formatMoney({ amount, override: { currency } });
        } catch {
            // Fall back to local formatting below when the SDK cannot format.
        }
    }
    // No SDK host (SSR, tests, non-Duda page): format with the platform Intl API.
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
        }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currency}`;
    }
}

function safeStr(
    str: EcommStr,
    key: string,
    fallback: string,
    replacements?: Record<string, string>
): string {
    const value = str(key, replacements);
    if (!value || value === key) return fallback;
    return value;
}

function getLocalizedRegion(
    str: EcommStr,
    country: string | null | undefined,
    region: string | null | undefined
): string | null {
    if (!country || !region) return null;
    return safeStr(
        str,
        `nativeEcommRuntime.countries.${country}.states.${region}`,
        region
    );
}

function getLocalizedCountry(
    str: EcommStr,
    country: string | null | undefined
): string | null {
    if (!country) return null;
    return safeStr(
        str,
        `nativeEcommRuntime.countries.${country}.name`,
        country
    );
}

function toFullAddressString(
    str: EcommStr,
    address1: string | null | undefined,
    address2: string | null | undefined,
    subLocality: string | null | undefined,
    city: string | null | undefined,
    region: string | null | undefined,
    country: string | null | undefined,
    postalCode: string | null | undefined
): string {
    let normalizedRegion = region;
    if (country === 'MX' && region === 'DIF') {
        normalizedRegion = 'CDMX';
    }

    const parts = [
        address1,
        address2,
        subLocality,
        city !== country ? city : null,
        getLocalizedRegion(str, country, normalizedRegion),
        getLocalizedCountry(str, country),
        postalCode !== country ? postalCode : null,
    ];

    return parts.filter(Boolean).join(', ');
}

export function formatPersonalAddress(
    str: EcommStr,
    addr: PersonalAddressResponseDto | null | undefined
): string {
    if (!addr) return '';
    return toFullAddressString(
        str,
        addr.address_1,
        addr.address_2,
        addr.sub_locality,
        addr.city,
        addr.region,
        addr.country,
        addr.postal_code
    );
}

export function formatBusinessAddress(
    str: EcommStr,
    addr: PickupLocationResponseDto['address'] | null | undefined
): string {
    if (!addr) return '';
    return toFullAddressString(
        str,
        addr.address_1,
        addr.address_2,
        null,
        addr.city,
        addr.region,
        addr.country,
        addr.postal_code
    );
}

const SEPARATOR_KEY = '__separator__';

export function getCustomFieldsByZone(
    order: OrderResponseDto
): Record<CustomFieldZoneResponseDto, OrderCustomFieldResponseDto[]> {
    const initial = {
        CONTACT_INFO: [],
        SHIPPING_ADDRESS: [],
        SHIPPING_METHODS: [],
        BILLING_ADDRESS: [],
        PAYMENT_METHODS: [],
    } as Record<CustomFieldZoneResponseDto, OrderCustomFieldResponseDto[]>;

    for (const field of order.custom_fields) {
        initial[field.zone].push(field);
    }
    return initial;
}

export function customFieldsToDataRows(
    fields: OrderCustomFieldResponseDto[]
): DataRow[] {
    const rows: DataRow[] = [];
    for (const field of fields) {
        rows.push({
            key: `custom-${field.id}`,
            label: field.label,
            value: field.value ?? undefined,
            isOption: true,
        });
    }
    return rows;
}

export function withSeparatorIfNonEmpty(extraRows: DataRow[]): DataRow[] {
    if (extraRows.length === 0) return [];
    return [{ key: SEPARATOR_KEY, isSeparator: true }, ...extraRows];
}

export function getPaymentMethodDisplayName(
    str: EcommStr,
    method: ConfirmedPaymentMethodResponseDto
): string {
    if (method.display_name) return method.display_name;
    return safeStr(
        str,
        `nativeEcommRuntime.paymentMethods.${method.name}`,
        method.name
    );
}

export function paymentMethodRowIsLoading(
    rowKey: string,
    args: {
        hasPaymentMethod: boolean;
        isRefreshing: boolean;
        shouldRefresh: boolean;
    }
): boolean {
    if (rowKey === 'title') return false;
    if (!args.hasPaymentMethod && (args.isRefreshing || args.shouldRefresh)) {
        return true;
    }
    return false;
}
